import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

interface FastingTrackerProps {
  completedDays: number[];
  missedDays: number[];
  onToggleFasted: (day: number) => void;
  onToggleMissed: (day: number) => void;
}

export const FastingTracker = ({ 
  completedDays, 
  missedDays, 
  onToggleFasted, 
  onToggleMissed 
}: FastingTrackerProps) => {
  const totalFasted = completedDays.length;
  const totalMissed = missedDays.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm">
            <span className="text-gold font-medium">{totalFasted}</span>
            <span className="text-muted-foreground"> fasted</span>
          </span>
          <span className="text-sm">
            <span className="text-rose-500 font-medium">{totalMissed}</span>
            <span className="text-muted-foreground"> missed</span>
          </span>
        </div>
      </div>

      {/* Calendar grid for 30 days */}
      <div className="grid grid-cols-10 gap-1.5">
        {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => {
          const isFasted = completedDays.includes(day);
          const isMissed = missedDays.includes(day);
          
          return (
            <motion.div
              key={day}
              className="relative group"
            >
              <motion.button
                onClick={() => onToggleFasted(day)}
                className={`
                  w-full aspect-square rounded-sm text-xs font-medium
                  flex items-center justify-center transition-all
                  ${isFasted 
                    ? "bg-gold text-white" 
                    : isMissed
                    ? "bg-rose-100 text-rose-500 border border-rose-200"
                    : "bg-cream text-muted-foreground hover:bg-accent"
                  }
                `}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {isFasted ? <Check className="w-3 h-3" /> : isMissed ? <X className="w-3 h-3" /> : day}
              </motion.button>
              
              {/* Option to mark as missed */}
              {!isFasted && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleMissed(day);
                  }}
                  className={`absolute -top-1 -right-1 w-3 h-3 rounded-full 
                    ${isMissed ? "bg-rose-500" : "bg-border opacity-0 group-hover:opacity-100"}
                    transition-opacity`}
                  title={isMissed ? "Unmark missed" : "Mark as missed"}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Tap to mark as fasted • Hover corner to mark as missed
      </p>
    </div>
  );
};
